import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Link, FileText, X, Upload, Plus, ExternalLink, Check } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../utils/firebase';
import { toast } from 'react-hot-toast';

const ReportResources = ({ resources = [], onChange, disabled = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [newLink, setNewLink] = useState({ title: '', url: '' });
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [activePreset, setActivePreset] = useState(null);

  // Preset link types
  const linkPresets = [
    { id: 'inspection', label: 'Inspection Report' },
    { id: 'action', label: 'Action List' },
    { id: 'notes', label: 'Additional Notes' },
    { id: 'sewerscope', label: 'Sewer Scope Video' },
    { id: 'radontest', label: 'Radon Test' }
  ];

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    const toastId = toast.loading('Uploading file...');

    try {
      const newResources = [...resources];
      
      for (const file of files) {
        // Generate a unique path for the file
        const timestamp = new Date().getTime();
        const randomStr = Math.random().toString(36).substring(2, 8);
        const filePath = `report_resources/${timestamp}_${randomStr}_${file.name}`;
        
        // Upload to Firebase Storage
        const fileRef = ref(storage, filePath);
        await uploadBytes(fileRef, file, { customMetadata: { owner: auth.currentUser?.uid || '' } });
        const downloadUrl = await getDownloadURL(fileRef);
        
        // Add to resources array
        newResources.push({
          type: 'file',
          title: file.name,
          url: downloadUrl,
          path: filePath,
          contentType: file.type,
          size: file.size,
          addedAt: new Date().toISOString()
        });
      }
      
      onChange(newResources);
      toast.success('File uploaded successfully', { id: toastId });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file', { id: toastId });
    } finally {
      setIsUploading(false);
      // Reset the file input
      e.target.value = '';
    }
  };

  const handleAddLink = () => {
    if (!newLink.title.trim() || !newLink.url.trim()) {
      toast.error('Please enter both a title and URL');
      return;
    }
    
    // Ensure URL has http/https
    let url = newLink.url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    const updatedResources = [
      ...resources,
      {
        type: 'link',
        title: newLink.title.trim(),
        url: url,
        addedAt: new Date().toISOString()
      }
    ];
    
    onChange(updatedResources);
    setNewLink({ title: '', url: '' });
    setShowLinkForm(false);
    toast.success('Link added successfully');
  };

  const handleRemoveResource = async (index) => {
    const resource = resources[index];
    const updatedResources = [...resources];
    updatedResources.splice(index, 1);
    
    try {
      // If it's a file, also delete from storage
      if (resource.type === 'file' && resource.path) {
        const fileRef = ref(storage, resource.path);
        await deleteObject(fileRef);
      }
      
      onChange(updatedResources);
      toast.success('Resource removed');
    } catch (error) {
      console.error('Error removing resource:', error);
      toast.error('Failed to remove resource');
    }
  };

  const handleSelectPreset = (presetId) => {
    setActivePreset(presetId);
    const preset = linkPresets.find(p => p.id === presetId);
    if (preset) {
      setNewLink({
        ...newLink,
        title: preset.label
      });
    }
  };

  return (
    <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full px-4 py-3 flex justify-between items-center bg-gray-100 hover:bg-gray-200 transition-colors text-left"
        disabled={disabled}
      >
        <div className="flex items-center">
          <FileText className="h-5 w-5 text-gray-600 mr-2" />
          <span className="font-medium text-gray-700">Additional Resources for Clients (Optional)</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-600" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-600" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            Add files or links that will be visible to clients in the portal.
          </p>

          {/* Current Resources */}
          {resources.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Current Resources:</h4>
              <ul className="space-y-2">
                {resources.map((resource, index) => (
                  <li 
                    key={index} 
                    className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                  >
                    <div className="flex items-center">
                      {resource.type === 'file' ? (
                        <FileText className="h-4 w-4 text-blue-500 mr-2" />
                      ) : (
                        <Link className="h-4 w-4 text-green-500 mr-2" />
                      )}
                      <div>
                        <p className="text-sm font-medium">{resource.title}</p>
                        <a 
                          href={resource.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center"
                        >
                          View <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveResource(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                      disabled={disabled}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Add Resources Buttons */}
          <div className="flex flex-wrap gap-2 mt-4">
            {/* File Upload Button */}
            <label className="cursor-pointer flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none">
              <Upload className="h-4 w-4 mr-2" />
              Upload File
              <input
                type="file"
                multiple
                className="hidden"
                onChange={handleFileUpload}
                disabled={isUploading || disabled}
              />
            </label>

            {/* Add Link Button */}
            <button
              type="button"
              onClick={() => setShowLinkForm(true)}
              className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              disabled={showLinkForm || disabled}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Link
            </button>
          </div>

          {/* Link Form */}
          {showLinkForm && (
            <div className="mt-4 p-3 bg-white rounded-md border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Add New Link</h4>
              
              <div className="space-y-3">
                <div>
                  <label htmlFor="link-title" className="block text-xs font-medium text-gray-700">
                    Title*
                  </label>
                  <div className="mt-1">
                    {/* Preset buttons in a single row */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {linkPresets.map(preset => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => handleSelectPreset(preset.id)}
                          className={`px-2 py-1 text-xs rounded border ${
                            activePreset === preset.id
                              ? 'bg-blue-100 border-blue-400 text-blue-700' 
                              : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    
                    {/* Text input field */}
                    <input
                      type="text"
                      id="link-title"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Enter title..."
                      value={newLink.title}
                      onChange={(e) => {
                        setNewLink({ ...newLink, title: e.target.value });
                        // If typing in the field, clear the active preset
                        if (activePreset) {
                          setActivePreset(null);
                        }
                      }}
                      disabled={disabled}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="link-url" className="block text-xs font-medium text-gray-700">
                    URL*
                  </label>
                  <input
                    type="text"
                    id="link-url"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="https://example.com"
                    value={newLink.url}
                    onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                    disabled={disabled}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowLinkForm(false)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50"
                    disabled={disabled}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddLink}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    disabled={disabled}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportResources; 